const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Flow Builder (Constructor de Flujos)",
    summary:
      "Diseña flujos de conversación interactivos y automatizados sin código. Conecta bloques visuales para responder preguntas, capturar variables, bifurcar por condiciones, procesar comprobantes con IA, consultar APIs externas y transferir clientes a departamentos o agentes.",
  },
  sections: [
    {
      id: "flujos_automatizados",
      title: "1. Gestión de Flujos de Automatización",
      description:
        "Crea, clona, exporta e importa flujos completos de chatbot para tus operaciones.",
      items: [
        {
          id: "crear_flujo",
          title: "Creación y Organización de Flujos",
          definition:
            "Permite crear flujos independientes asociados a marcas, departamentos o campañas. Cada flujo cuenta con un lienzo infinito, control de versiones, guardado manual y posibilidad de importación/exportación en archivo comprimido .zip.",
        },
        {
          id: "editor_nodos",
          title: "Lienzo Visual y Conexiones",
          definition:
            "El lienzo permite arrastrar y soltar nodos desde la barra lateral izquierda, interconectando salidas y entradas mediante cables lógicos. Soporta zoom, encuadre rápido, deshacer/rehacer y navegación táctil en dispositivos móviles.",
        },
      ],
    },
    {
      id: "disparadores",
      title: "2. Disparadores y Conexiones",
      description:
        "Configuración de disparadores por palabras clave, colas y canales de mensajería.",
      items: [
        {
          id: "palabras_clave",
          title: "Palabras Clave (Triggers)",
          definition:
            "Define las frases o términos que activan el flujo automáticamente. Puede configurarse en modo 'Coincidencia Exacta' (el cliente debe enviar únicamente esa palabra) o 'Contiene' (el flujo se dispara si la frase se encuentra en cualquier parte del mensaje).",
        },
        {
          id: "canales_integrados",
          title: "Asignación a Conexiones",
          definition:
            "Vincula el flujo a una o varias conexiones activas de WhatsApp (Baileys o API Cloud Oficial), Telegram, Facebook Messenger o Instagram Direct. Solo responderá a través de los canales seleccionados.",
        },
      ],
    },
    {
      id: "nodos_basicos",
      title: "3. Nodos Básicos",
      description:
        "Bloques estructurales de arranque y coordinación inicial del flujo.",
      items: [
        {
          id: "node_start",
          title: "Nodo Inicio (Start)",
          definition:
            "Punto de entrada obligatorio de todo flujo. Se crea automáticamente y no puede eliminarse. Cuando el cliente cumple las condiciones del disparador, la ejecución parte inmediatamente desde su conector de salida hacia el primer nodo conectado.",
        },
      ],
    },
    {
      id: "nodos_contenido",
      title: "4. Nodos de Contenido Multimedia",
      description:
        "Bloques para el envío directo de mensajes de texto enriquecido y archivos adjuntos.",
      items: [
        {
          id: "node_singleBlock",
          title: "Bloque Único (Contenedor Secuencial)",
          definition:
            "Contenedor inteligente que agrupa múltiples mensajes (texto, imágenes, audios y videos) dentro de una misma tarjeta visual. Permite estructurar respuestas complejas sin saturar el lienzo con decenas de nodos individuales.",
        },
        {
          id: "node_message",
          title: "Mensaje de Texto",
          definition:
            "Envía un mensaje de texto plano con soporte completo para formato WhatsApp (*negrita*, _cursiva_, ~tachado~, `código`), emoticonos y sustitución de variables dinámicas entre llaves dobles (ejemplo: {{name}}, {{phone}}, {{email}} o variables personalizadas).",
        },
        {
          id: "node_video",
          title: "Video",
          definition:
            "Envía un archivo de video en formato MP4 o WebM a la conversación. Permite subir el video directamente a la plataforma o ingresar una URL pública de alojamiento, con opción de pie de video descriptivo.",
        },
        {
          id: "node_img",
          title: "Imagen",
          definition:
            "Transmite imágenes en formatos JPG, PNG o WebP. Soporta texto de pie de foto (caption) con variables dinámicas, ideal para catálogos, banners promocionales o infografías de soporte.",
        },
        {
          id: "node_doc",
          title: "Documento",
          definition:
            "Envía archivos descargables como PDF, hojas de cálculo de Excel (.xlsx), documentos Word (.docx) o archivos comprimidos .zip. Permite definir el nombre visible con el que el cliente descargará el archivo.",
        },
        {
          id: "node_audio",
          title: "Nota de Voz / Audio PTT",
          definition:
            "Envía archivos de audio en formato MP3 u OGG. Puede configurarse como 'Nota de Voz grabada en el momento' (Push-To-Talk) para que aparezca con el icono de micrófono verde en WhatsApp, logrando un trato ultra humano y cercano.",
        },
      ],
    },
    {
      id: "nodos_interaccion",
      title: "5. Nodos de Interacción y Captura",
      description:
        "Bloques para detener el flujo, capturar respuestas del usuario o simular digitación.",
      items: [
        {
          id: "node_input",
          title: "Entrada / Pregunta (Input)",
          definition:
            "Pausa el flujo y formula una pregunta al cliente, esperando su respuesta. El texto escrito por el cliente se almacena automáticamente en una variable personalizada (ejemplo: 'direccion_entrega') para utilizarse en nodos posteriores, APIs o CRM.",
        },
        {
          id: "node_interval",
          title: "Pausa / Intervalo (Digitando...)",
          definition:
            "Genera una espera programada de X segundos o minutos antes de enviar el siguiente mensaje. Durante la pausa muestra el estado 'escribiendo...' o 'grabando audio...' en WhatsApp, otorgando un ritmo de conversación natural.",
        },
        {
          id: "node_form",
          title: "Formulario Web Dinámico",
          definition:
            
            "Envía un enlace único web al cliente ({link}) y pone el bot en pausa silenciosa. Al completarse y enviarse desde el navegador, todas las respuestas se inyectan automáticamente en las variables del flujo ({{nombre_campo}} en minúsculas) y la conversación continúa inmediatamente por el conector de salida.",
        }
      ],
    },
    {
      id: "nodos_logica",
      title: "6. Nodos de Lógica y Control de Flujo",
      description:
        "Bifurcaciones inteligentes, comparaciones condicionales, splits y saltos en el lienzo.",
      items: [
        {
          id: "node_randomizer",
          title: "Split A/B Aleatorio",
          definition:
            "Divide el tráfico de usuarios en porcentajes configurables (ejemplo: 50% Camino A y 50% Camino B). Fundamental para realizar pruebas A/B de mensajes de venta o distribuir equitativamente leads entre diferentes equipos.",
        },
        {
          id: "node_conditionCompare",
          title: "Condición Lógica (IF / ELSE)",
          definition:
            "Evalúa variables o respuestas del usuario mediante operadores lógicos: es igual a, no es igual, contiene, no contiene, empieza con, termina con, mayor que, menor que, está vacío o no está vacío. Bifurca el flujo por la salida 'Verdadero' o 'Falso'.",
        },
        {
          id: "node_listMatch",
          title: "Búsqueda en Lista (List Match)",
          definition:
            "Compara la respuesta del usuario contra una lista de elementos predefinidos o variables de array. Si encuentra coincidencia exacta o parcial, enruta al usuario por el conector de éxito y exporta el elemento seleccionado a una variable.",
        },
        {
          id: "node_jump",
          title: "Salto de Flujo (Jump)",
          definition:
            "Redirige la ejecución inmediatamente a cualquier otro nodo existente en el lienzo sin necesidad de trazar cables largos y confusos. Mantiene el diagrama limpio, modular y fácil de mantener.",
        },
      ],
    },
    {
      id: "nodos_sistema",
      title: "7. Nodos de Sistema y Gestión de CRM",
      description:
        "Acciones automáticas sobre tickets, contactos, etiquetas, agentes y tableros Kanban.",
      items: [
        {
          id: "node_ticket",
          title: "Transferir a Cola / Fila",
          definition:
            "Transfiere la conversación a una cola o departamento específico (Ventas, Soporte, Facturación). Asigna los permisos correspondientes y notifica a los operadores asignados a dicha fila.",
        },
        {
          id: "node_tag",
          title: "Asignar Etiqueta (Tag)",
          definition:
            "Aplica una o múltiples etiquetas al contacto en tiempo real (ejemplo: 'Cliente VIP', 'Interesado en Plan Pro', 'Campaña Black Friday') para segmentación avanzada y campañas de remarketing.",
        },
        {
          id: "node_removeTag",
          title: "Remover Etiqueta (Remove Tag)",
          definition:
            "Retira etiquetas previamente asignadas al contacto cuando cambia su estatus (ejemplo: remover 'Pendiente de Pago' al confirmarse la compra).",
        },
        {
          id: "node_saveContact",
          title: "Guardar / Actualizar Contacto",
          definition:
            "Actualiza permanentemente el nombre, correo electrónico, documento de identidad o campos personalizados del contacto en la libreta de direcciones utilizando los valores recolectados durante la conversación.",
        },
        {
          id: "node_switchFlow",
          title: "Cambiar de Flujo",
          definition:
            "Finaliza la ejecución del flujo actual y transfiere al usuario a otro flujo de automatización activo, iniciando desde el nodo Start del nuevo flujo.",
        },
        {
          id: "node_attendant",
          title: "Asignar Agente / Atendente",
          definition:
            "Asigna el ticket de atención de forma directa y nominal a un operador o agente específico de la empresa, sacándolo de la cola general.",
        },
        {
          id: "node_kanban",
          title: "Mover en Embudo Kanban",
          definition:
            "Mueve la tarjeta del ticket a una columna específica del tablero Kanban (ejemplo: 'Nuevo Lead' -> 'En Negociación' -> 'Cerrado Ganado'), actualizando las métricas comerciales.",
        },
        {
          id: "node_ticketStatus",
          title: "Cambiar Estado de Ticket",
          definition:
            "Modifica el estado operativo del ticket en la plataforma: Abierto (atendiéndose), Pendiente (esperando acción) o Cerrado/Resuelto (finaliza el ticket y libera al asesor).",
        },
      ],
    },
    {
      id: "nodos_integraciones",
      title: "8. Nodos de Integraciones e Inteligencia Artificial",
      description:
        "Conectividad con servicios externos, webhooks, validación con IA, agenda y modelos GPT.",
      items: [
        {
          id: "node_httpRequest",
          title: "Webhook / Petición HTTP REST",
          definition:
            "Realiza llamadas HTTP (GET, POST, PUT, DELETE, PATCH) a cualquier API externa, CRM o backend. Permite enviar cabeceras personalizadas, cuerpo JSON con variables dinámicas y mapear las respuestas JSON de retorno a variables del flujo.",
        },
        {
          id: "node_variable",
          title: "Operaciones con Variables",
          definition:
            "Crea nuevas variables, concatena textos, realiza operaciones matemáticas o asigna valores por defecto. Esencial para transformar datos antes de enviarlos a una API o mostrarlos al cliente.",
        },
        {
          id: "node_regex",
          title: "Extractor de Patrones Regex",
          definition:
            "Aplica expresiones regulares sobre la entrada del usuario para extraer con precisión números telefónicos, correos electrónicos, números de DNI/RUT, códigos postales o palabras clave dentro de un texto largo.",
        },
        {
          id: "node_openai",
          title: "Inteligencia Artificial (OpenAI / GPT)",
          definition:
            "Conecta un agente inteligente alimentado por modelos GPT. Puede operar en modo permanente (conversación libre con base de conocimiento RAG y salida por intenciones) o modo temporal (cumple una tarea específica y regresa al flujo tradicional).",
        },
        {
          id: "node_paymentValidation",
          title: "Reconocimiento y Validación de Pagos IA",
          definition:
            "Analiza imágenes de comprobantes bancarios y billeteras digitales (Yape, Plin, transferencias bancarias, recibos) mediante visión artificial. Extrae automáticamente monto, fecha, hora, número de operación y valida contra duplicados.",
        },
        {
          id: "node_scheduling",
          title: "Agendamiento de Citas",
          definition:
            "Consulta en tiempo real la disponibilidad de horarios de los asesores o servicios de la empresa, permitiendo al cliente seleccionar una fecha y hora para agendar una cita directamente desde WhatsApp.",
        },
        {
          id: "node_notifier",
          title: "Notificador WhatsApp Silencioso",
          definition:
            "Envía una notificación de alerta por WhatsApp a un número supervisor, técnico o comercial interno (ejemplo: '¡Nuevo pago validado de $100!') sin que el cliente note ninguna alteración en su chat.",
        },
        {
          id: "node_retargeting",
          title: "Retargeting Programado",
          definition:
            "Programa el envío de un mensaje de seguimiento futuro si el cliente no continúa el flujo tras X horas o días, ideal para recuperar carritos abandonados o clientes indecisos.",
        },
        {
          id: "node_remarketing",
          title: "Remarketing Comercial",
          definition:
            "Pausa el flujo y agenda secuencias de nutrición comercial periódicas. Si el cliente contesta en cualquier momento, el sistema reanuda el flujo de conversación de inmediato.",
        },
      ],
    },
    {
      id: "nodos_interactivos",
      title: "9. Nodos Interactivos de WhatsApp",
      description:
        "Formatos nativos interactivos de alta conversión (Botones, Listas, Carruseles y Encuestas).",
      items: [
        {
          id: "node_buttonInteractive",
          title: "Botones Interactivos y CTA",
          definition:
            "Envía un mensaje con hasta 3 botones de respuesta rápida para que el cliente seleccione con un solo toque, o botones de llamado a la acción (CTA) que abren enlaces web o llamadas telefónicas directas.",
        },
        {
          id: "node_carouselInteractive",
          title: "Carrusel Multitarjeta",
          definition:
            "Envía una galería horizontal deslizante de hasta 10 tarjetas interactivas. Cada tarjeta incluye su propia imagen, título, descripción y botones independientes para cotizar o comprar.",
        },
        {
          id: "node_listInteractive",
          title: "Lista Desplegable (Menú Interactivo)",
          definition:
            "Muestra un botón de menú que despliega una lista organizada en secciones con hasta 10 opciones seleccionables, evitando que el usuario tenga que escribir números o palabras para navegar.",
        },
        {
          id: "node_poll",
          title: "Encuesta Nativa de WhatsApp",
          definition:
            "Envía una encuesta oficial interactiva con una pregunta central y múltiples opciones de votación. Permite configurar voto único o múltiple y bifurcar el flujo según la opción votada por el usuario.",
        },
      ],
    },
  ],
};

const en = {
  header: {
    badge: "BotMix Official Guide",
    title: "Flow Builder",
    summary:
      "Design interactive and automated conversation flows without code. Connect visual blocks to answer queries, capture variables, branch conditionally, validate payment slips with AI, call external REST APIs, and route customers to queues or human agents.",
  },
  sections: [
    {
      id: "flujos_automatizados",
      title: "1. Automation Flow Management",
      description:
        "Create, clone, export, and import complete chatbot automation flows for your business operations.",
      items: [
        {
          id: "crear_flujo",
          title: "Flow Creation and Organization",
          definition:
            "Build independent flows assigned to specific brands, departments, or marketing campaigns. Every flow includes an infinite canvas, version control, manual saving, and .zip export/import support.",
        },
        {
          id: "editor_nodos",
          title: "Visual Canvas and Connections",
          definition:
            "Drag and drop nodes from the left sidebar and link input/output handles using logical wires. Includes zoom controls, fit view, undo/redo history, and touch navigation on mobile devices.",
        },
      ],
    },
    {
      id: "disparadores",
      title: "2. Triggers and Connections",
      description:
        "Configure inbound triggers based on keywords, queues, and connected communication channels.",
      items: [
        {
          id: "palabras_clave",
          title: "Keywords (Triggers)",
          definition:
            "Specify phrases or terms that activate the flow automatically. Supports 'Exact Match' (the customer message must equal the keyword) and 'Contains' (triggers whenever the phrase is found anywhere in the text).",
        },
        {
          id: "canales_integrados",
          title: "Channel Assignment",
          definition:
            "Link the flow to one or more active WhatsApp channels (Baileys or Official Cloud API), Telegram, Facebook Messenger, or Instagram Direct.",
        },
      ],
    },
    {
      id: "nodos_basicos",
      title: "3. Basic Nodes",
      description:
        "Structural starter blocks coordinating the initial execution of the automation.",
      items: [
        {
          id: "node_start",
          title: "Start Node",
          definition:
            "Mandatory entry point for every flow. It is created automatically and cannot be deleted. Once trigger conditions are satisfied, execution starts immediately through its output connector toward the first connected node.",
        },
      ],
    },
    {
      id: "nodos_contenido",
      title: "4. Media and Content Nodes",
      description:
        "Blocks dedicated to dispatching rich text messages, media files, and attachments.",
      items: [
        {
          id: "node_singleBlock",
          title: "Single Block (Sequential Container)",
          definition:
            "Smart multi-message container that groups text, images, voice notes, and videos within a single visual card, keeping the canvas neat and uncluttered.",
        },
        {
          id: "node_message",
          title: "Text Message",
          definition:
            "Dispatches plain or formatted WhatsApp text (*bold*, _italic_, ~strikethrough~, `code`) with full emoji support and dynamic variable placeholders like {{name}}, {{phone}}, {{email}}, or custom variables.",
        },
        {
          id: "node_video",
          title: "Video",
          definition:
            "Sends an MP4 or WebM video file to the conversation via direct file upload or public hosted URL, with optional descriptive captions.",
        },
        {
          id: "node_img",
          title: "Image",
          definition:
            "Transmits JPG, PNG, or WebP graphic files. Supports dynamic variable captions, making it ideal for catalogs, promotion banners, or visual tutorials.",
        },
        {
          id: "node_doc",
          title: "Document",
          definition:
            "Sends downloadable files such as PDF invoices, Excel spreadsheets (.xlsx), Word documents (.docx), or compressed archives (.zip) with customizable download filenames.",
        },
        {
          id: "node_audio",
          title: "Voice Note / Audio (PTT)",
          definition:
            "Sends MP3 or OGG audio. Can be configured as a real-time recorded voice note (PTT) with the green WhatsApp microphone badge for an authentic, human-like interaction.",
        },
      ],
    },
    {
      id: "nodos_interaccion",
      title: "5. Interaction and Capture Nodes",
      description:
        "Blocks to pause execution, wait for user inputs, or simulate typing behavior.",
      items: [
        {
          id: "node_input",
          title: "Input / Question",
          definition:
            "Pauses the flow and asks a question to the customer. The incoming customer answer is stored inside a custom variable (e.g., 'delivery_address') for downstream logic or CRM storage.",
        },
        {
          id: "node_interval",
          title: "Interval / Pause (Typing...)",
          definition:
            "Creates a planned delay of X seconds or minutes before sending the next message while displaying 'typing...' or 'recording audio...' in WhatsApp.",
        },
        {
          id: "node_form",
          title: "Dynamic Web Form",
          definition:
            
            "Sends a unique web form link ({link}) and puts the bot in silent pause. Upon browser submission, all answers are injected into lowercase flow variables ({{field_name}}) and the bot immediately resumes via the output connector.",
        }
      ],
    },
    {
      id: "nodos_logica",
      title: "6. Logic and Flow Control Nodes",
      description:
        "Smart branching, conditional IF/ELSE checks, randomized splits, and jump portals.",
      items: [
        {
          id: "node_randomizer",
          title: "Random Split A/B",
          definition:
            "Splits incoming traffic according to custom percentages (e.g., 50% Path A, 50% Path B) for A/B testing sales pitches or distributing leads evenly across teams.",
        },
        {
          id: "node_conditionCompare",
          title: "Logical Condition (IF / ELSE)",
          definition:
            "Evaluates variables against logical rules: equals, not equals, contains, starts with, ends with, greater than, less than, or empty. Branches the conversation into 'True' and 'False' handles.",
        },
        {
          id: "node_listMatch",
          title: "List Match Search",
          definition:
            "Compares customer text against a predefined list of options or array items. If a match is found, proceeds via the success handle and outputs the matched item into a variable.",
        },
        {
          id: "node_jump",
          title: "Jump (Node Portal)",
          definition:
            "Instantly jumps flow execution to any designated target node on the canvas without drawing tangled wires across your diagram.",
        },
      ],
    },
    {
      id: "nodos_sistema",
      title: "7. System and CRM Nodes",
      description:
        "Automated actions operating directly on tickets, contact profiles, tags, agents, and Kanban pipelines.",
      items: [
        {
          id: "node_ticket",
          title: "Transfer to Queue",
          definition:
            "Routes the conversation to a specific department or queue (Sales, Support, Billing), notifying the respective department attendants.",
        },
        {
          id: "node_tag",
          title: "Assign Tag",
          definition:
            "Attaches one or multiple tags to the contact in real time (e.g., 'VIP Client', 'Interested in Pro Plan') for segmentation and broadcast campaigns.",
        },
        {
          id: "node_removeTag",
          title: "Remove Tag",
          definition:
            "Removes specific tags from the contact when their journey advances (e.g., clearing 'Pending Payment' once transaction is verified).",
        },
        {
          id: "node_saveContact",
          title: "Save / Update Contact",
          definition:
            "Saves the customer's full name, email, tax ID, or custom field values collected during the conversation into the permanent contact book.",
        },
        {
          id: "node_switchFlow",
          title: "Switch Flow",
          definition:
            "Gracefully closes the active flow and transfers control over to another independent automation flow, starting from its Start node.",
        },
        {
          id: "node_attendant",
          title: "Assign Attendant / Agent",
          definition:
            "Assigns the support ticket directly to a designated team member or agent by name, bypassing the round-robin queue.",
        },
        {
          id: "node_kanban",
          title: "Move in Kanban Pipeline",
          definition:
            "Transfers the ticket card to a specified column in the Kanban pipeline (e.g., 'New Lead' -> 'Negotiation' -> 'Closed Won').",
        },
        {
          id: "node_ticketStatus",
          title: "Update Ticket Status",
          definition:
            "Changes the operational state of the ticket: Open, Pending, or Closed/Resolved.",
        },
      ],
    },
    {
      id: "nodos_integraciones",
      title: "8. Integrations and AI Nodes",
      description:
        "Webhooks, external REST APIs, AI payment recognition, appointments, and OpenAI GPT agents.",
      items: [
        {
          id: "node_httpRequest",
          title: "Webhook / HTTP REST Request",
          definition:
            "Executes HTTP calls (GET, POST, PUT, DELETE, PATCH) to third-party APIs, passing custom headers and JSON payloads with dynamic variables, then mapping response fields back to flow variables.",
        },
        {
          id: "node_variable",
          title: "Variable Operations",
          definition:
            "Creates, concatenates, computes, or transforms internal variables at runtime before sending them to APIs or showing them to the customer.",
        },
        {
          id: "node_regex",
          title: "Regex Pattern Extractor",
          definition:
            "Applies regular expressions on user messages to isolate phone numbers, email addresses, IDs, or tracking codes.",
        },
        {
          id: "node_openai",
          title: "OpenAI GPT Agent",
          definition:
            "Connects a conversational AI powered by GPT models with system prompt instructions and RAG knowledge bases, capable of intent classification and tool execution.",
        },
        {
          id: "node_paymentValidation",
          title: "AI Payment Verification",
          definition:
            "Scans payment receipt screenshots and bank transfers using computer vision, extracting amount, date, time, and transaction reference code while detecting duplicate submissions.",
        },
        {
          id: "node_scheduling",
          title: "Appointment Scheduling",
          definition:
            "Checks live calendar availability for agents or services and lets customers select convenient time slots directly over WhatsApp.",
        },
        {
          id: "node_notifier",
          title: "Silent WhatsApp Notifier",
          definition:
            "Dispatches internal alerts or transactional summaries to an admin or supervisor WhatsApp number without altering the customer's chat.",
        },
        {
          id: "node_retargeting",
          title: "Scheduled Retargeting",
          definition:
            "Schedules automated follow-up messages after X hours or days if the user abandons the funnel, perfect for recovering lost leads.",
        },
        {
          id: "node_remarketing",
          title: "Commercial Remarketing",
          definition:
            "Pauses the flow and schedules periodic outreach messages, resuming interaction automatically if the customer responds.",
        },
      ],
    },
    {
      id: "nodos_interactivos",
      title: "9. Interactive WhatsApp Nodes",
      description:
        "Native high-converting interactive elements (Buttons, Lists, Carousels, and Polls).",
      items: [
        {
          id: "node_buttonInteractive",
          title: "Interactive Buttons and CTA",
          definition:
            "Sends messages with up to 3 quick-reply buttons or Call-to-Action (CTA) buttons linking to external web URLs or initiating direct phone calls.",
        },
        {
          id: "node_carouselInteractive",
          title: "Multi-card Carousel",
          definition:
            "Transmits a horizontal swipeable gallery with up to 10 product cards, each with its own image, description, and action buttons.",
        },
        {
          id: "node_listInteractive",
          title: "Dropdown Interactive List",
          definition:
            "Displays a menu button opening an interactive sectioned list of up to 10 selectable options.",
        },
        {
          id: "node_poll",
          title: "Native WhatsApp Poll",
          definition:
            "Dispatches an official interactive WhatsApp poll with single or multiple choice voting options, branching according to the selected answer.",
        },
      ],
    },
  ],
};

const pt = {
  header: {
    badge: "Guia Oficial BotMix",
    title: "Flow Builder (Construtor de Fluxos)",
    summary:
      "Crie fluxos de conversa interativos e automatizados sem código. Conecte blocos visuais para responder dúvidas, capturar variáveis, ramificar por condições, validar comprovantes com IA, consultar APIs REST externas e transferir clientes para setores ou atendentes humanos.",
  },
  sections: [
    {
      id: "flujos_automatizados",
      title: "1. Gestão de Fluxos de Automação",
      description:
        "Crie, clone, exporte e importe fluxos completos de chatbot para as suas operações comerciais.",
      items: [
        {
          id: "crear_flujo",
          title: "Criação e Organização de Fluxos",
          definition:
            "Permite criar fluxos independentes vinculados a marcas, setores ou campanhas. Cada fluxo possui tela infinita, histórico de alterações, salvamento e exportação/importação em arquivo .zip.",
        },
        {
          id: "editor_nodos",
          title: "Tela Visual e Conexões",
          definition:
            "Arraste e solte nós da barra lateral e conecte portas de entrada e saída por meio de cabos lógicos. Inclui zoom, enquadramento rápido, desfazer/refazer e controle por toque em dispositivos móveis.",
        },
      ],
    },
    {
      id: "disparadores",
      title: "2. Gatilhos e Conexões",
      description:
        "Configuração de gatilhos automáticos por palavras-chave, filas e canais de atendimento.",
      items: [
        {
          id: "palabras_clave",
          title: "Palavras-chave (Gatilhos)",
          definition:
            "Define os termos ou frases que iniciam o fluxo automaticamente. Suporta 'Correspondência Exata' ou 'Contém' (inicia se a frase estiver em qualquer parte da mensagem).",
        },
        {
          id: "canales_integrados",
          title: "Vínculo com Conexões",
          definition:
            "Associa o fluxo a uma ou mais conexões ativas do WhatsApp (Baileys ou API Oficial em Nuvem), Telegram, Facebook Messenger ou Instagram Direct.",
        },
      ],
    },
    {
      id: "nodos_basicos",
      title: "3. Nós Básicos",
      description:
        "Blocos estruturais de partida e coordenação inicial do fluxo.",
      items: [
        {
          id: "node_start",
          title: "Nó de Início (Start)",
          definition:
            "Ponto de entrada obrigatório de qualquer fluxo. Criado automaticamente e não pode ser excluído. Quando o cliente ativa o gatilho, a execução parte imediatamente pelo seu conector de saída.",
        },
      ],
    },
    {
      id: "nodos_contenido",
      title: "4. Nós de Conteúdo Multimídia",
      description:
        "Blocos para o envio direto de mensagens de texto formatadas e arquivos anexos.",
      items: [
        {
          id: "node_singleBlock",
          title: "Bloco Único (Contêiner Sequencial)",
          definition:
            "Contêiner inteligente que agrupa mensagens de texto, imagens, áudios e vídeos em um único cartão visual, mantendo a tela organizada e limpa.",
        },
        {
          id: "node_message",
          title: "Mensagem de Texto",
          definition:
            "Envia mensagens de texto com suporte para formatação do WhatsApp (*negrito*, _itálico_, ~tachado~, `código`), emojis e interpolação de variáveis dinâmicas (ex: {{name}}, {{phone}}, {{email}}).",
        },
        {
          id: "node_video",
          title: "Vídeo",
          definition:
            "Envia arquivos de vídeo MP4 ou WebM por upload direto ou URL pública, com opção de legenda descritiva.",
        },
        {
          id: "node_img",
          title: "Imagem",
          definition:
            "Transmite fotos nos formatos JPG, PNG ou WebP com legendas personalizadas com variáveis, ideal para catálogos e promoções.",
        },
        {
          id: "node_doc",
          title: "Documento",
          definition:
            "Envia arquivos como boletos PDF, planilhas Excel (.xlsx), documentos Word ou pastas compactadas .zip, com nome personalizado para download.",
        },
        {
          id: "node_audio",
          title: "Áudio / Gravado na Hora (PTT)",
          definition:
            "Envia áudio MP3 ou OGG. Pode ser configurado como gravação de voz instantânea (PTT) com o microfone verde do WhatsApp para máxima aproximação humana.",
        },
      ],
    },
    {
      id: "nodos_interaccion",
      title: "5. Nós de Interação e Coleta de Dados",
      description:
        "Blocos para pausar o fluxo, receber dados do cliente ou simular digitação.",
      items: [
        {
          id: "node_input",
          title: "Entrada / Pergunta (Input)",
          definition:
            "Pausa o fluxo e aguarda a resposta do cliente, armazenando o texto em uma variável personalizada (ex: 'endereco_entrega') para uso posterior.",
        },
        {
          id: "node_interval",
          title: "Pausa / Intervalo (Digitando...)",
          definition:
            "Cria uma espera programada de X segundos ou minutos mostrando o status 'digitando...' ou 'gravando áudio...' no WhatsApp para dar naturalidade à conversa.",
        },
        {
          id: "node_form",
          title: "Formulário Web Dinâmico",
          definition:
            
            "Envia um link exclusivo ({link}) e coloca o bot em pausa silenciosa. Ao enviar no navegador, todas as respostas são injetadas nas variáveis do fluxo ({{nome_campo}} em minúsculas) e a conversa é retomada imediatamente pelo conector de saída.",
        }
      ],
    },
    {
      id: "nodos_logica",
      title: "6. Nós de Lógica e Controle de Fluxo",
      description:
        "Bifurcações inteligentes, comparações condicionais, testes A/B e saltos.",
      items: [
        {
          id: "node_randomizer",
          title: "Divisão Aleatória A/B",
          definition:
            "Distribui os clientes em porcentagens configuráveis (ex: 50% Caminho A, 50% Caminho B) para testar abordagens de vendas ou dividir leads entre atendentes.",
        },
        {
          id: "node_conditionCompare",
          title: "Condição Lógica (IF / ELSE)",
          definition:
            "Compara variáveis através de operadores lógicos: igual a, diferente, contém, inicia com, maior, menor ou vazio, direcionando pelos conectores Verdadeiro ou Falso.",
        },
        {
          id: "node_listMatch",
          title: "Busca em Lista (List Match)",
          definition:
            "Compara a resposta do cliente contra uma lista pré-definida de opções. Se houver correspondência, segue pelo caminho de sucesso salvando o item em variável.",
        },
        {
          id: "node_jump",
          title: "Salto de Fluxo (Jump)",
          definition:
            "Transfere a execução imediatamente para outro nó do diagrama sem cruzar cabos compridos na tela, tornando a estrutura limpa e modular.",
        },
      ],
    },
    {
      id: "nodos_sistema",
      title: "7. Nós de Sistema e CRM",
      description:
        "Ações automáticas sobre chamados, perfis de contatos, etiquetas, atendentes e Kanban.",
      items: [
        {
          id: "node_ticket",
          title: "Transferir para Fila / Setor",
          definition:
            "Encaminha a conversa para um setor específico (Comercial, Suporte, Financeiro), notificando os atendentes correspondentes.",
        },
        {
          id: "node_tag",
          title: "Adicionar Etiqueta (Tag)",
          definition:
            "Aplica etiquetas ao contato em tempo real (ex: 'Cliente VIP', 'Interesse Plano Pro') para segmentação e disparos futuros.",
        },
        {
          id: "node_removeTag",
          title: "Remover Etiqueta",
          definition:
            "Remove etiquetas do contato quando a etapa for concluída (ex: remover 'Aguardando Pagamento').",
        },
        {
          id: "node_saveContact",
          title: "Salvar / Atualizar Contato",
          definition:
            "Salva nome, e-mail, documento ou campos personalizados coletados na conversa diretamente na ficha do cliente.",
        },
        {
          id: "node_switchFlow",
          title: "Alternar Fluxo",
          definition:
            "Encerra o fluxo atual e transfere o cliente para outro fluxo de automação, iniciando no nó Start do novo fluxo.",
        },
        {
          id: "node_attendant",
          title: "Atribuir Atendente Nominal",
          definition:
            "Direciona o atendimento diretamente para um usuário específico da equipe, sem passar pela fila geral.",
        },
        {
          id: "node_kanban",
          title: "Mover no Funil Kanban",
          definition:
            "Move o cartão do ticket para uma coluna do funil de vendas (ex: 'Lead' -> 'Proposta' -> 'Fechado Ganho').",
        },
        {
          id: "node_ticketStatus",
          title: "Alterar Status do Ticket",
          definition:
            "Muda o estado do chamado: Aberto, Pendente ou Fechado/Resolvido.",
        },
      ],
    },
    {
      id: "nodos_integraciones",
      title: "8. Nós de Integrações e Inteligência Artificial",
      description:
        "Webhooks, requisições HTTP REST, validação de pagamentos com visão IA e agentes GPT.",
      items: [
        {
          id: "node_httpRequest",
          title: "Webhook / Requisição HTTP REST",
          definition:
            "Executa chamadas HTTP (GET, POST, PUT, DELETE, PATCH) para sistemas externos, enviando cabeçalhos e payloads JSON com variáveis e capturando respostas nas variáveis do fluxo.",
        },
        {
          id: "node_variable",
          title: "Operações com Variáveis",
          definition:
            "Cria, concatena, calcula ou transforma variáveis em tempo de execução antes de enviar para APIs ou exibir ao cliente.",
        },
        {
          id: "node_regex",
          title: "Extrator Regex",
          definition:
            "Aplica expressões regulares na mensagem do cliente para extrair CPFs, números de telefone, e-mails ou códigos de rastreio.",
        },
        {
          id: "node_openai",
          title: "Inteligência Artificial (OpenAI GPT)",
          definition:
            "Integra um agente inteligente GPT com base de conhecimento RAG para responder perguntas contextuais ou classificar intenções.",
        },
        {
          id: "node_paymentValidation",
          title: "Reconhecimento de Comprovantes com IA",
          definition:
            "Analisa fotos de comprovantes bancários e PIX por visão computacional, extraindo valor, data, autenticação e conferindo duplicidades.",
        },
        {
          id: "node_scheduling",
          title: "Agendamento de Horários",
          definition:
            "Consulta horários disponíveis no calendário da empresa e permite que o cliente reserve consultas ou reuniões pelo WhatsApp.",
        },
        {
          id: "node_notifier",
          title: "Notificador Silencioso WhatsApp",
          definition:
            "Envia alertas internos no WhatsApp para supervisores ou gestores sem interferir na conversa do cliente.",
        },
        {
          id: "node_retargeting",
          title: "Retargeting Programado",
          definition:
            "Programa mensagens de recuperação após X horas ou dias caso o cliente não conclua o atendimento.",
        },
        {
          id: "node_remarketing",
          title: "Remarketing Comercial",
          definition:
            "Pausa o fluxo e agenda mensagens de acompanhamento periódico, retomando se o cliente responder.",
        },
      ],
    },
    {
      id: "nodos_interactivos",
      title: "9. Nós Interativos do WhatsApp",
      description:
        "Formatos nativos interativos de alta conversão (Botões, Listas, Carrosséis e Enquetes).",
      items: [
        {
          id: "node_buttonInteractive",
          title: "Botões Interativos e CTA",
          definition:
            "Envia mensagens com até 3 botões de resposta rápida com um toque ou botões de chamada para ação com links e telefones.",
        },
        {
          id: "node_carouselInteractive",
          title: "Carrossel Multi-cartão",
          definition:
            "Envia galeria horizontal com até 10 cartões interativos, cada um com imagem, título, texto e botões próprios.",
        },
        {
          id: "node_listInteractive",
          title: "Lista Interativa Suspensa",
          definition:
            "Apresenta um botão de menu que abre uma lista organizada em seções com até 10 opções selecionáveis.",
        },
        {
          id: "node_poll",
          title: "Enquete Nativa do WhatsApp",
          definition:
            "Envia enquete oficial interativa com pergunta e opções de voto único ou múltiplo, ramificando o fluxo pelo voto escolhido.",
        },
      ],
    },
  ],
};

const fr = {
  header: {
    badge: "Guide Officiel BotMix",
    title: "Flow Builder (Créateur de Flux)",
    summary:
      "Concevez des flux de conversation interactifs et automatisés sans code. Reliez des blocs visuels pour répondre aux questions, capturer des variables, créer des embranchements logiques, valider des preuves de paiement par IA, appeler des API REST externes et transférer des clients vers des services ou des agents humains.",
  },
  sections: [
    {
      id: "flujos_automatizados",
      title: "1. Gestion des Flux d'Automatisation",
      description:
        "Créez, dupliquez, exportez et importez des flux complets de chatbot pour vos activités commerciales.",
      items: [
        {
          id: "crear_flujo",
          title: "Création et Organisation des Flux",
          definition:
            "Permet d'élaborer des parcours indépendants associés à des marques ou départements avec canevas infini, historique des versions et sauvegarde manuelle ou export .zip.",
        },
        {
          id: "editor_nodos",
          title: "Éditeur Visuel et Connexions",
          definition:
            "Glissez-déposez des nœuds depuis la barre latérale et reliez-les grâce à des câbles logiques avec zoom, réinitialisation de vue et historique défaire/refaire.",
        },
      ],
    },
    {
      id: "disparadores",
      title: "2. Déclencheurs et Connexions",
      description:
        "Configuration des déclencheurs par mots-clés, files d'attente et canaux de discussion.",
      items: [
        {
          id: "palabras_clave",
          title: "Mots-clés (Triggers)",
          definition:
            "Définit les phrases qui déclenchent le parcours automatiquement. Prend en charge la 'Correspondance Exacte' ou 'Contient'.",
        },
        {
          id: "canales_integrados",
          title: "Affectation aux Canaux",
          definition:
            "Associe le flux à des canaux actifs WhatsApp (Baileys ou API Cloud Officielle), Telegram, Facebook Messenger ou Instagram Direct.",
        },
      ],
    },
    {
      id: "nodos_basicos",
      title: "3. Nœuds de Base",
      description:
        "Blocs structurants pour démarrer le parcours de conversation.",
      items: [
        {
          id: "node_start",
          title: "Nœud de Départ (Start)",
          definition:
            "Point d'entrée obligatoire créé automatiquement. Dès que le déclencheur est actif, l'exécution débute vers le premier nœud connecté.",
        },
      ],
    },
    {
      id: "nodos_contenido",
      title: "4. Nœuds de Contenu Multimédia",
      description:
        "Envoi de messages texte enrichis et de pièces jointes.",
      items: [
        {
          id: "node_singleBlock",
          title: "Bloc Unique (Conteneur Séquentiel)",
          definition:
            "Conteneur regroupant textes, images, vocaux et vidéos au sein d'une seule carte visuelle pour garder le canevas fluide.",
        },
        {
          id: "node_message",
          title: "Message Texte",
          definition:
            "Envoie des messages avec formatage WhatsApp (*gras*, _italique_, emojis) et remplacement de variables dynamiques (ex: {{name}}, {{phone}}).",
        },
        {
          id: "node_video",
          title: "Vidéo",
          definition:
            "Envoie un fichier vidéo MP4/WebM par téléversement direct ou lien public avec légende optionnelle.",
        },
        {
          id: "node_img",
          title: "Image",
          definition:
            "Transmet des photos JPG/PNG/WebP avec légendes textuelles personnalisées par variables.",
        },
        {
          id: "node_doc",
          title: "Document",
          definition:
            "Envoie des documents PDF, feuilles Excel ou archives .zip avec nom de fichier personnalisable.",
        },
        {
          id: "node_audio",
          title: "Message Vocal / Audio (PTT)",
          definition:
            "Diffuse des fichiers MP3 ou vocaux instantanés (micro vert WhatsApp) pour une expérience humaine et chaleureuse.",
        },
      ],
    },
    {
      id: "nodos_interaccion",
      title: "5. Nœuds d'Interaction et de Saisie",
      description:
        "Mise en pause du parcours, collecte de réponses et simulation d'écriture.",
      items: [
        {
          id: "node_input",
          title: "Saisie / Question (Input)",
          definition:
            "Met en pause le flux pour poser une question au client et enregistre sa réponse dans une variable personnalisée.",
        },
        {
          id: "node_interval",
          title: "Intervalle / Pause (En train d'écrire...)",
          definition:
            "Définit un délai de temporisation simulant l'écriture ou l'enregistrement vocal pour un rythme naturel.",
        },
        {
          id: "node_form",
          title: "Formulaire Web Dynamique",
          definition:
            
            "Envoie un lien web unique ({link}) et met le bot en pause silencieuse. À la soumission dans le navigateur, les réponses alimentent les variables du flux ({{nom_du_champ}} en minuscules) et la conversation reprend immédiatement par le connecteur de sortie.",
        }
      ],
    },
    {
      id: "nodos_logica",
      title: "6. Nœuds de Logique et Contrôle",
      description:
        "Embranchements conditionnels, fractionnement A/B et sauts de flux.",
      items: [
        {
          id: "node_randomizer",
          title: "Répartition Aléatoire A/B",
          definition:
            "Distribue les utilisateurs selon des pourcentages configurables pour tester des arguments de vente.",
        },
        {
          id: "node_conditionCompare",
          title: "Condition Logique (IF / ELSE)",
          definition:
            "Compare les valeurs avec des opérateurs (égal, contient, supérieur, vide) et oriente vers Vrai ou Faux.",
        },
        {
          id: "node_listMatch",
          title: "Recherche par Liste (List Match)",
          definition:
            "Vérifie la saisie du client par rapport à une liste de choix et extrait la correspondance.",
        },
        {
          id: "node_jump",
          title: "Saut de Flux (Jump)",
          definition:
            "Transfère l'exécution directement vers un autre nœud sans encombrer le canevas de câbles.",
        },
      ],
    },
    {
      id: "nodos_sistema",
      title: "7. Nœuds Système et CRM",
      description:
        "Actions directes sur les tickets, contacts, étiquettes, conseillers et pipelines Kanban.",
      items: [
        {
          id: "node_ticket",
          title: "Transférer vers une File",
          definition:
            "Oriente le chat vers un département dédié (Support, Ventes, Facturation) avec notification aux agents.",
        },
        {
          id: "node_tag",
          title: "Ajouter une Étiquette (Tag)",
          definition:
            "Applique des étiquettes au contact pour segmenter vos listes d'audience.",
        },
        {
          id: "node_removeTag",
          title: "Supprimer une Étiquette",
          definition:
            "Retire une étiquette lorsque l'étape client a évolué.",
        },
        {
          id: "node_saveContact",
          title: "Enregistrer / Modifier le Contact",
          definition:
            "Met à jour le nom, l'email ou les champs personnalisés dans le carnet d'adresses.",
        },
        {
          id: "node_switchFlow",
          title: "Basculer vers un Autre Flux",
          definition:
            "Clôture le flux courant pour diriger le client vers un autre parcours d'automatisation.",
        },
        {
          id: "node_attendant",
          title: "Assigner un Conseiller",
          definition:
            "Attribue nominalement la conversation à un agent d'assistance précis.",
        },
        {
          id: "node_kanban",
          title: "Déplacer dans le Pipeline Kanban",
          definition:
            "Déplace la carte du ticket dans une colonne spécifique du tableau Kanban.",
        },
        {
          id: "node_ticketStatus",
          title: "Modifier le Statut du Ticket",
          definition:
            "Change le statut opérationnel : Ouvert, En attente ou Fermé/Résolu.",
        },
      ],
    },
    {
      id: "nodos_integraciones",
      title: "8. Nœuds d'Intégration et Intelligence Artificielle",
      description:
        "Appels Webhook REST, validation IA des reçus de paiement et agents conversationnels GPT.",
      items: [
        {
          id: "node_httpRequest",
          title: "Webhook / Requête HTTP REST",
          definition:
            "Exécute des requêtes GET, POST, PUT ou DELETE avec transmission de JSON et enregistrement des réponses en variables.",
        },
        {
          id: "node_variable",
          title: "Opérations sur Variables",
          definition:
            "Crée, concatène, calcule ou ajuste les valeurs des variables dynamiques.",
        },
        {
          id: "node_regex",
          title: "Extracteur Regex",
          definition:
            "Applique des expressions régulières pour isoler des numéros de suivi, emails ou identifiants.",
        },
        {
          id: "node_openai",
          title: "Agent IA (OpenAI / GPT)",
          definition:
            "Active un agent conversationnel doté d'instructions et d'une base RAG pour traiter des demandes complexes.",
        },
        {
          id: "node_paymentValidation",
          title: "Validation de Paiement par IA",
          definition:
            "Analyse les captures de virements et reçus par vision artificielle, en extrayant montant, date et code d'opération.",
        },
        {
          id: "node_scheduling",
          title: "Prise de Rendez-vous",
          definition:
            "Consulte les créneaux libres de l'équipe et permet la réservation directe sur WhatsApp.",
        },
        {
          id: "node_notifier",
          title: "Notification WhatsApp Silencieuse",
          definition:
            "Alerte un responsable ou administrateur par WhatsApp sans modifier la discussion du client.",
        },
        {
          id: "node_retargeting",
          title: "Retargeting Programmé",
          definition:
            "Envoie des relances automatiques programmées en cas d'inactivité pour relancer les prospects.",
        },
        {
          id: "node_remarketing",
          title: "Remarketing Commercial",
          definition:
            "Met en pause le flux et planifie des suivis commerciaux, reprenant dès que le client répond.",
        },
      ],
    },
    {
      id: "nodos_interactivos",
      title: "9. Nœuds Interactifs WhatsApp",
      description:
        "Composants interactifs à haute conversion (Boutons, Listes, Carrousels et Sondages).",
      items: [
        {
          id: "node_buttonInteractive",
          title: "Boutons Interactifs et CTA",
          definition:
            "Envoie jusqu'à 3 boutons de réponse rapide en un clic ou des boutons d'appel à l'action ouvrant des URL.",
        },
        {
          id: "node_carouselInteractive",
          title: "Carrousel Multicartes",
          definition:
            "Transmet une galerie horizontale jusqu'à 10 cartes composées d'images, descriptions et boutons.",
        },
        {
          id: "node_listInteractive",
          title: "Liste Interactive Déroulante",
          definition:
            "Présente un menu interactif affichant jusqu'à 10 options structurées en sections.",
        },
        {
          id: "node_poll",
          title: "Sondage Natif WhatsApp",
          definition:
            "Envoie un sondage officiel interactif avec choix unique ou multiple, orientant le flux selon le vote.",
        },
      ],
    },
  ],
};

const ar = {
  header: {
    badge: "دليل BotMix الرسمي",
    title: "منشئ التدفقات (Flow Builder)",
    summary:
      "صمم مسارات محادثة تفاعلية ومؤتمتة بالكامل بدون أي كود برمجي. اربط العقد المرئية للإجابة على الاستفسارات، والتقاط المتغيرات، والتفريع الشرطي، والتحقق من إيصالات الدفع بالذكاء الاصطناعي، والاتصال بواجهات API خارجية، وتوجيه العملاء إلى الأقسام والموظفين.",
  },
  sections: [
    {
      id: "flujos_automatizados",
      title: "1. إدارة تدفقات الأتمتة",
      description:
        "إنشاء واستنساخ وتصدير واستيراد تدفقات المحادثة التفاعلية لإدارة الأعمال والمبيعات.",
      items: [
        {
          id: "crear_flujo",
          title: "إنشاء التدفقات وتنظيمها",
          definition:
            "يتيح بناء مسارات منفصلة مرتبطة بالعلامات التجارية أو الحملات مع لوحة تحكم غير محدودة وسجل إصدارات وحفظ يدوي وإمكانية التصدير والاستيراد كملف .zip.",
        },
        {
          id: "editor_nodos",
          title: "لوحة التصميم المرئية والروابط",
          definition:
            "سحب وإفلات العقد من القائمة الجانبية وتوصيل المنافذ بكابلات منطقية مع دعم التكبير والمحاذاة والتراجع والتنقل السلس عبر الهواتف الذكية.",
        },
      ],
    },
    {
      id: "disparadores",
      title: "2. المشغلات والقنوات",
      description:
        "تهيئة المشغلات التلقائية بناءً على الكلمات المفتاحية وأقسام الدعم وقنوات المراسلة.",
      items: [
        {
          id: "palabras_clave",
          title: "الكلمات المفتاحية (Triggers)",
          definition:
            "تحديد العبارات التي تبدأ التدفق تلقائياً، مع دعم نمط 'المطابقة التامة' أو نمط 'يحتوي على' في أي جزء من نص الرسالة.",
        },
        {
          id: "canales_integrados",
          title: "الربط بقنوات الاتصال",
          definition:
            "ربط التدفق بقنوات واتساب النشطة (Baileys أو واجهة Meta السحابية الرسمية)، وتيليجرام، وفيسبوك ماسنجر، وانستغرام دايركت.",
        },
      ],
    },
    {
      id: "nodos_basicos",
      title: "3. العقد الأساسية",
      description:
        "العقدة الهيكلية لبدء وانطلاق مسار المحادثة الآلية.",
      items: [
        {
          id: "node_start",
          title: "عقدة البداية (Start)",
          definition:
            "نقطة الانطلاق الإلزامية لكل تدفق وتنشأ تلقائياً ولا يمكن حذفها، حيث تنطلق الرسائل فور تحقق شروط المشغل.",
        },
      ],
    },
    {
      id: "nodos_contenido",
      title: "4. عقد المحتوى والوسائط",
      description:
        "إرسال النصوص المنسقة والملفات ومقاطع الفيديو والرسائل الصوتية.",
      items: [
        {
          id: "node_singleBlock",
          title: "الكتلة الموحدة (Single Block)",
          definition:
            "حاوية ذكية تجمع عدة رسائل نصوص وصور وفيديو وتسجيلات صوتية داخل بطاقة مرئية واحدة للمحافظة على ترتيب اللوحة.",
        },
        {
          id: "node_message",
          title: "رسالة نصية",
          definition:
            "إرسال نص عادي أو منسق بخصائص واتساب (*غامق*، _مائل_) والرموز التعبيرية مع دمج المتغيرات الديناميكية مثل {{name}} و {{phone}}.",
        },
        {
          id: "node_video",
          title: "مقطع فيديو",
          definition:
            "إرسال مقطع فيديو بصيغة MP4 أو WebM عبر الرفع المباشر أو الروابط العامة مع تعليق توضيحي.",
        },
        {
          id: "node_img",
          title: "صورة",
          definition:
            "إرسال صور JPG و PNG مع نصوص توضيحية تدعم المتغيرات، مثالية للكتالوجات والعروض الترويجية.",
        },
        {
          id: "node_doc",
          title: "مستند وملف",
          definition:
            "إرسال ملفات PDF وجداول Excel ومستندات Word مع تخصيص اسم الملف عند التحميل.",
        },
        {
          id: "node_audio",
          title: "رسالة صوتية (PTT)",
          definition:
            "إرسال صوت MP3 أو بصيغة رسالة صوتية مسجلة باللحظة الحالية بعلامة الميكروفون الأخضر لمنح طابع إنساني وواقعي.",
        },
      ],
    },
    {
      id: "nodos_interaccion",
      title: "5. عقد التفاعل والتقاط البيانات",
      description:
        "إيقاف التدفق لانتظار إجابة العميل أو محاكاة الكتابة الطبيعية.",
      items: [
        {
          id: "node_input",
          title: "حقل إدخال / سؤال (Input)",
          definition:
            "يوقف التدفق لطرح سؤال على العميل ويحفظ رده تلقائياً داخل متغير مخصص لاستخدامه لاحقاً.",
        },
        {
          id: "node_interval",
          title: "فاصل زمني / توقف مؤقت (جاري الكتابة...)",
          definition:
            "توليد فترة انتظار لبضع ثوانٍ أو دقائق مع إظهار حالة 'جاري الكتابة...' على واتساب لجعل المحادثة طبيعية.",
        },
        {
          id: "node_form",
          title: "استمارة ويب ديناميكية",
          definition:
            
            "يرسل رابط ويب فريد ({link}) ويضع البوت في وضع الإيقاف المؤقت الصامت. عند إرسال النموذج، تُحقن جميع الإجابات تلقائياً في متغيرات التدفق ({{field_name}} بأحرف صغيرة) وتستأنف المحادثة سيرها فوراً عبر منفذ الخروج.",
        }
      ],
    },
    {
      id: "nodos_logica",
      title: "6. عقد المنطق والتحكم في المسار",
      description:
        "التفريع الشرطي، والمقارنات المنطقية، وتقسيم الزوار، والانتقال السريع.",
      items: [
        {
          id: "node_randomizer",
          title: "تقسيم عشوائي (Split A/B)",
          definition:
            "توزيع العملاء بنسب مئوية محددة لاختبار أساليب البيع المختلفة وتوزيع جهات الاتصال على المسارات.",
        },
        {
          id: "node_conditionCompare",
          title: "شرط منطقي (IF / ELSE)",
          definition:
            "مقارنة المتغيرات بواسطة معاملات منطقية (يساوي، يحتوي، أكبر، فارغ) والتوجيه عبر مخرج صحيح أو خطأ.",
        },
        {
          id: "node_listMatch",
          title: "مطابقة القوائم (List Match)",
          definition:
            "فحص رد العميل ومطابقته مع قائمة خيارات محددة مسبقاً وتوجيه التدفق عند وجود تطابق.",
        },
        {
          id: "node_jump",
          title: "انتقال سريع (Jump)",
          definition:
            "نقل مسار المحادثة فوراً إلى أي عقدة أخرى على اللوحة دون الحاجة لتمديد كابلات طويلة ومعقدة.",
        },
      ],
    },
    {
      id: "nodos_sistema",
      title: "7. عقد النظام وإدارة علاقات العملاء (CRM)",
      description:
        "إجراءات تلقائية على التذاكر، وبيانات جهات الاتصال، والوسوم، والموظفين، ولوحات كانبان.",
      items: [
        {
          id: "node_ticket",
          title: "تحويل إلى قسم / قائمة انتظار",
          definition:
            "توجيه المحادثة إلى قسم معين (المبيعات، الدعم الفني، الحسابات) مع إشعار الموظفين المختصين.",
        },
        {
          id: "node_tag",
          title: "إضافة وسم (Tag)",
          definition:
            "إضافة وسوم لجهة الاتصال في الوقت الفعلي لتصنيف الجمهور والحملات الإعلانية.",
        },
        {
          id: "node_removeTag",
          title: "إزالة وسم",
          definition:
            "حذف وسوم محددة من جهة الاتصال عند انتقال العميل إلى مرحلة جديدة.",
        },
        {
          id: "node_saveContact",
          title: "حفظ / تحديث جهة الاتصال",
          definition:
            "تحديث اسم العميل أو بريده أو حقله المخصص في دفتر العناوين بالبيانات التي تم جمعها.",
        },
        {
          id: "node_switchFlow",
          title: "التحويل إلى تدفق آخر",
          definition:
            "إنهاء التدفق الحالي وتحويل العميل لمسار أتمتة آخر ينطلق من عقدة البداية الخاصة به.",
        },
        {
          id: "node_attendant",
          title: "تعيين موظف محدد",
          definition:
            "إسناد التذكرة بالاسم إلى موظف دعم معين داخل الفريق دون المرور بالقائمة العامة.",
        },
        {
          id: "node_kanban",
          title: "نقل في مسار كانبان (Kanban)",
          definition:
            "نقل بطاقة العميل إلى مرحلة محددة في قمع المبيعات (مثل: مهتم -> قيد التفاوض -> تم الإغلاق).",
        },
        {
          id: "node_ticketStatus",
          title: "تغيير حالة التذكرة",
          definition:
            "تعديل الحالة التشغيلية للطلب: مفتوح، معلق، أو مغلق/تم الحل.",
        },
      ],
    },
    {
      id: "nodos_integraciones",
      title: "8. عقد التكامل والذكاء الاصطناعي",
      description:
        "طلبات الويب هوك، وواجهات REST، والتحقق من المدفوعات برؤية الحاسوب، ونماذج GPT.",
      items: [
        {
          id: "node_httpRequest",
          title: "ويب هوك / طلب HTTP REST",
          definition:
            "تنفيذ طلبات برمجية (GET, POST, PUT, DELETE) للربط مع الأنظمة الخارجية وتخزين ردود JSON داخل المتغيرات.",
        },
        {
          id: "node_variable",
          title: "عمليات المتغيرات",
          definition:
            "إنشاء متغيرات جديدة أو دمج النصوص أو إجراء عمليات حسابية برمجية أثناء المحادثة.",
        },
        {
          id: "node_regex",
          title: "مستخرج الأنماط (Regex)",
          definition:
            "تطبيق تعابير نمطية لاستخراج أرقام الهواتف أو البريد الإلكتروني أو أرقام الهوية من نصوص الرسائل.",
        },
        {
          id: "node_openai",
          title: "وكيل الذكاء الاصطناعي (OpenAI GPT)",
          definition:
            "تفعيل محادثة ذكية بالاعتماد على نماذج GPT وقواعد المعرفة للإجابة عن الأسئلة المعقدة.",
        },
        {
          id: "node_paymentValidation",
          title: "التحقق من إيصالات الدفع بالذكاء الاصطناعي",
          definition:
            "فحص صور الحوالات والإيصالات المصرفية بواسطة الرؤية الحاسوبية واستخراج المبلغ والتاريخ ورقم العملية.",
        },
        {
          id: "node_scheduling",
          title: "حجز المواعيد",
          definition:
            "التحقق من الأوقات المتاحة في جدول المواعيد وتمكين العميل من حجز موعده عبر واتساب.",
        },
        {
          id: "node_notifier",
          title: "إشعار واتساب صامت للإدارة",
          definition:
            "إرسال تنبيه فوري ومخفي لرقم الإدارة عند تحقق حدث هام دون أن يلاحظ العميل أي تغيير.",
        },
        {
          id: "node_retargeting",
          title: "إعادة الاستهداف المجدولة",
          definition:
            "جدولة رسائل متابعة تلقائية بعد ساعات أو أيام في حال عدم إكمال العميل لمسار الشراء.",
        },
        {
          id: "node_remarketing",
          title: "إعادة التسويق التجاري",
          definition:
            "إيقاف التدفق مؤقتاً ومتابعة العميل برسائل دورية، واستئناف المحادثة فور رد العميل.",
        },
      ],
    },
    {
      id: "nodos_interactivos",
      title: "9. عقد واتساب التفاعلية",
      description:
        "عناصر تفاعلية مدمجة ذات معدل تحويل عالي (أزرار، قوائم، معارض صور واستطلاعات).",
      items: [
        {
          id: "node_buttonInteractive",
          title: "أزرار تفاعلية وروابط (CTA)",
          definition:
            "إرسال رسائل تحتوي على حتى 3 أزرار رد سريع بنقرة واحدة أو أزرار فتح روابط هاتفية ومواقع إلكترونية.",
        },
        {
          id: "node_carouselInteractive",
          title: "معرض بطاقات تمرير أفقي (Carrusel)",
          definition:
            "إرسال ما يصل إلى 10 بطاقات تمرير تفاعلية، تحتوي كل بطاقة على صورة وعنوان ووصف وأزرار خاصة بها.",
        },
        {
          id: "node_listInteractive",
          title: "قائمة خيارات منسدلة",
          definition:
            "زر قائمة تفاعلي يعرض ما يصل إلى 10 خيارات مرتبة في أقسام تمنع العميل من عناء كتابة الأرقام.",
        },
        {
          id: "node_poll",
          title: "استطلاع رأي رسمي على واتساب",
          definition:
            "إرسال استطلاع رأي تفاعلي مع خيارات تصويت مفرد أو متعدد وتوجيه المسار بناءً على الخيار المحدد.",
        },
      ],
    },
  ],
};

export const FLOW_BUILDER_HELP_DATA = { es, en, pt, fr, ar };
export default FLOW_BUILDER_HELP_DATA;
